/**
 * Voice AI — LLM Settings Sub-Component
 * Dual provider management: Main LLM + Contextual LLM (fast acknowledger).
 * Each section has its own provider list, Add Provider button, and inline edit form.
 */

import { LitElement, html, css } from 'lit';
import { designSystem, componentStyles } from '../../styles/design-system.js';
import { settingsStyles } from '../../styles/settings-styles.js';
import { iconStyles } from '../../styles/icon-styles.js';
import { llmService } from '../../services/llm-service.js';
import { confirmService } from '../../services/confirm-service.js';

export class LlmSettings extends LitElement {
  static properties = {
    providers: { type: Array },
    contextualProviders: { type: Array },
    contextualEnabled: { type: Boolean },
    _togglingContextual: { state: true },
    // Edit state — tracks which section ('main' | 'contextual') and which key ('new' | key suffix)
    _editTarget: { state: true },
    _editing: { state: true },
    _form: { state: true },
    _saving: { state: true },
    _deleting: { state: true },
    _showApiKey: { state: true },
    _setAsDefault: { state: true },
  };

  static styles = [designSystem, componentStyles, settingsStyles, css`
    .browse-model-wrap {
      display: flex;
      gap: var(--space-2);
      align-items: center;
    }
    .browse-model-wrap .form-input {
      flex: 1;
      font-family: var(--font-mono);
      font-size: var(--text-xs);
    }
    .browse-btn {
      flex-shrink: 0;
      white-space: nowrap;
    }
  `];

  constructor() {
    super();
    this.providers = [];
    this.contextualProviders = [];
    this.contextualEnabled = false;
    this._togglingContextual = false;
    this._editTarget = null;   // 'main' | 'contextual' | null
    this._editing = null;      // null | 'new' | key suffix
    this._form = {};
    this._saving = false;
    this._deleting = false;
    this._showApiKey = false;
    this._setAsDefault = false;
  }

  render() {
    return html`
      ${iconStyles}
      <div class="content-header">
        <h2 class="content-title">AI Provider Setup</h2>
        <p class="content-description">Configure language model providers for AI-powered voice conversations.</p>
      </div>

      <!-- ═══ Main LLM Providers ═══ -->
      <div class="settings-section">
        <div class="section-title-row">
          <i class="fa-sharp-duotone fa-solid fa-brain-circuit section-icon"></i>
          <span class="section-title">Configured Providers</span>
        </div>
        <p class="section-description">Your active LLM providers. Click a provider to edit its configuration.</p>

        ${this.providers.length > 0 ? html`
          <div class="provider-list">
            ${this.providers.map(p => this._renderProviderItem(p, 'main'))}
          </div>
        ` : html`
          <div class="empty-hint">
            <i class="fa-sharp fa-light fa-brain-circuit"></i>
            No providers configured yet. Add your first LLM provider below.
          </div>
        `}

        <div class="add-btn" @click=${() => this._startAdd('main')}>
          <i class="fa-sharp fa-light fa-plus"></i>
          Add Provider
        </div>

        ${this._editTarget === 'main' && this._editing !== null ? this._renderEditSection('main') : ''}
      </div>

      <!-- ═══ Contextual LLM Providers ═══ -->
      <div class="settings-section">
        <div class="section-title-row" style="justify-content:space-between;">
          <div style="display:flex;align-items:center;gap:var(--space-2);">
            <i class="fa-sharp-duotone fa-solid fa-bolt section-icon" style="color:var(--color-success);"></i>
            <span class="section-title">Contextual LLM</span>
          </div>
          <div style="display:flex;align-items:center;gap:var(--space-2);">
            <span style="font-size:var(--text-xs);color:var(--color-text-tertiary);">
              ${this.contextualEnabled ? 'Enabled' : 'Disabled'}
            </span>
            <div
              class="toggle ${this.contextualEnabled ? 'active' : ''}"
              style="${this._togglingContextual ? 'opacity:0.5;pointer-events:none;' : ''}"
              @click=${this._toggleContextualEnabled}
            ></div>
          </div>
        </div>
        <p class="section-description">
          A fast LLM that generates instant contextual acknowledgments while the main LLM processes requests.
          Instead of generic "Got it!", callers hear natural responses like "Let me check what we have near the pool area."
          ${this.contextualEnabled ? 'Configure a fast, small model here — ideally one with <200ms response time.' : ''}
        </p>

        ${this.contextualEnabled ? html`
          ${this.contextualProviders.length > 0 ? html`
            <div class="provider-list">
              ${this.contextualProviders.map(p => this._renderProviderItem(p, 'contextual'))}
            </div>
          ` : html`
            <div class="empty-hint">
              <i class="fa-sharp fa-light fa-bolt"></i>
              No contextual LLM configured. Add a fast provider below.
            </div>
          `}

          <div class="add-btn" @click=${() => this._startAdd('contextual')}>
            <i class="fa-sharp fa-light fa-plus"></i>
            Add Provider
          </div>

          ${this._editTarget === 'contextual' && this._editing !== null ? this._renderEditSection('contextual') : ''}
        ` : ''}
      </div>

    `;
  }

  /* ─── Provider Item ─── */

  _renderProviderItem(provider, target) {
    const def = llmService.getProviderDef(provider.provider);
    const icon = def ? def.icon : 'fa-code';
    const providerName = def ? def.name : provider.provider;
    const isActive = this._editTarget === target && this._editing === provider.key;

    return html`
      <div class="provider-item ${isActive ? 'active' : ''}" @click=${() => this._startEdit(provider, target)}>
        <div class="provider-info">
          <div class="provider-icon">
            <i class="fa-sharp-duotone fa-solid ${icon}"></i>
          </div>
          <div class="provider-details">
            <div class="provider-name">
              ${provider.label}
              <span style="color:var(--color-text-tertiary);font-size:var(--text-xs);font-weight:400;">${providerName}</span>
            </div>
            <div class="provider-meta">${provider.model}</div>
          </div>
        </div>
        <div class="provider-right">
          ${provider.api_key_status === 'configured'
            ? html`<span class="badge badge-success">Key Set</span>`
            : html`<span class="badge badge-warning">No Key</span>`
          }
          ${provider.is_default ? html`<span class="badge badge-primary">Default</span>` : ''}
          <i class="fa-sharp fa-light fa-pen edit-icon"></i>
        </div>
      </div>
    `;
  }

  /* ─── Edit / Add Form ─── */

  _renderEditSection(target) {
    const isNew = this._editing === 'new';
    const defs = llmService.getProviderDefinitions();
    const selectedDef = defs.find(d => d.id === this._form.provider) || defs[0];
    const models = selectedDef.models || [];
    const isCustom = selectedDef.id === 'custom';
    const isContextual = target === 'contextual';

    return html`
      <div class="settings-section" style="margin-top:var(--space-4);border:1px solid var(--color-border-primary);border-radius:var(--radius-lg);padding:var(--space-5);">
        <div class="section-title-row">
          <i class="fa-sharp-duotone fa-solid ${isNew ? 'fa-plus-circle' : 'fa-pen-to-square'} section-icon"></i>
          <span class="section-title">${isNew ? (isContextual ? 'Add Contextual Provider' : 'Add New Provider') : 'Edit Provider'}</span>
        </div>
        <p class="section-description">${isNew
          ? (isContextual ? 'Configure a fast LLM for instant contextual acknowledgments.' : 'Configure a new LLM provider with API credentials and model settings.')
          : 'Update the configuration for this provider.'
        }</p>

        <div class="form-grid">
          <div class="form-group">
            <label class="form-label">Provider</label>
            <select class="form-select"
              .value=${this._form.provider || ''}
              @change=${e => this._onProviderChange(e.target.value)}
              ?disabled=${!isNew}
            >
              ${defs.map(d => html`<option value=${d.id} ?selected=${d.id === this._form.provider}>${d.name}</option>`)}
            </select>
          </div>
          <div class="form-group">
            <label class="form-label">Label</label>
            <input class="form-input" type="text"
              placeholder="${isContextual ? 'e.g. Groq Fast Ack' : 'e.g. GPT-4o Realtime'}"
              .value=${this._form.label || ''}
              @input=${e => this._form = { ...this._form, label: e.target.value }}
            />
            <div class="form-hint">Friendly name to identify this provider.</div>
          </div>
        </div>

        ${selectedDef.noApiKey ? html`
          <div style="margin-top:var(--space-4);padding:12px 16px;background:#F7FAF4;border:1px solid #bbd7b5;border-radius:8px;font-size:13px;color:#2C6C11;">
            <i class="fa-sharp fa-light fa-circle-check" style="margin-right:6px;"></i>
            Uses the Claude CLI installed on this server. No API key required — runs on your existing subscription.
          </div>
        ` : html`
          <div class="form-grid full" style="margin-top:var(--space-4);">
            <div class="form-group">
              <label class="form-label">API Key</label>
              <div class="password-wrap">
                <input class="form-input"
                  type="${this._showApiKey ? 'text' : 'password'}"
                  placeholder=${selectedDef.placeholder || 'Enter API key'}
                  .value=${this._form.api_key || ''}
                  @input=${e => this._form = { ...this._form, api_key: e.target.value }}
                />
                <button class="password-toggle" @click=${() => this._showApiKey = !this._showApiKey}>
                  <i class="fa-sharp fa-light ${this._showApiKey ? 'fa-eye-slash' : 'fa-eye'}"></i>
                </button>
              </div>
              ${!isNew && this._form._masked ? html`
                <div class="form-hint">Current key: ${this._form._masked}. Leave blank to keep existing key.</div>
              ` : html`
                <div class="form-hint">Your API key. Keep this secret.</div>
              `}
            </div>
          </div>
        `}

        ${isCustom ? html`
          <div class="form-grid full" style="margin-top:var(--space-4);">
            <div class="form-group">
              <label class="form-label">Base URL</label>
              <input class="form-input" type="text"
                placeholder="https://api.example.com/v1"
                .value=${this._form.base_url || ''}
                @input=${e => this._form = { ...this._form, base_url: e.target.value }}
              />
              <div class="form-hint">Custom API endpoint URL.</div>
            </div>
          </div>
        ` : ''}

        <div class="form-grid full" style="margin-top:var(--space-4);">
          <div class="form-group">
            <label class="form-label">Model</label>
            ${isCustom ? html`
              <input class="form-input" type="text"
                placeholder="model-name"
                .value=${this._form.model || ''}
                @input=${e => this._form = { ...this._form, model: e.target.value }}
              />
            ` : selectedDef.hasBrowseModels ? html`
              <div class="browse-model-wrap">
                <input class="form-input" type="text"
                  placeholder="Click Browse to select a model"
                  .value=${this._form.model || ''}
                  @input=${e => this._form = { ...this._form, model: e.target.value }}
                />
                <button class="btn btn-secondary browse-btn"
                  @click=${() => this._dispatch('browse-models', { target, provider: this._form.provider, apiKey: this._form.api_key || '', providerKey: this._editing !== 'new' ? this._editing : '' })}
                  type="button">
                  <i class="fa-sharp fa-light fa-magnifying-glass"></i>
                  Browse
                </button>
              </div>
            ` : html`
              <select class="form-select"
                .value=${this._form.model || ''}
                @change=${e => this._form = { ...this._form, model: e.target.value }}
              >
                ${models.map(m => html`<option value=${m} ?selected=${m === this._form.model}>${m}</option>`)}
              </select>
            `}
            <div class="form-hint">${selectedDef.hasBrowseModels ? 'Browse to select from live catalog, or type model ID manually.' : 'Model to use for this provider.'}</div>
          </div>
        </div>

        ${selectedDef.hasMcpConfig ? html`
          <div class="form-grid full" style="margin-top:var(--space-4);">
            <div class="form-group">
              <label class="form-label">
                <i class="fa-sharp fa-light fa-server" style="margin-right:4px;color:var(--color-text-tertiary);"></i>
                MCP Server Configuration
              </label>
              <textarea class="form-input" rows="8"
                style="font-family:'JetBrains Mono',monospace;font-size:12px;min-height:140px;resize:vertical;line-height:1.5;"
                placeholder='{"filesystem":{"command":"npx","args":["-y","@anthropic/mcp-filesystem","/path"]}}'
                .value=${this._form.mcp_servers_json || '{}'}
                @input=${e => this._form = { ...this._form, mcp_servers_json: e.target.value }}
              ></textarea>
              <div class="form-hint">JSON object defining MCP servers. Each key is a server name with "command" and "args" fields. Tools from these servers are merged with voice tools.</div>
            </div>
          </div>
        ` : ''}

        <div class="form-actions">
          <button class="btn btn-primary" @click=${this._handleSave} ?disabled=${this._saving}>
            <i class="fa-sharp fa-light ${this._saving ? 'fa-spinner-third fa-spin' : 'fa-check'}"></i>
            ${this._saving ? 'Saving...' : 'Save Provider'}
          </button>
          ${isNew ? html`
            <label class="checkbox-inline" style="display:flex;align-items:center;gap:var(--space-2);cursor:pointer;">
              <input type="checkbox" .checked=${this._setAsDefault}
                @change=${e => this._setAsDefault = e.target.checked} />
              Set as default
            </label>
          ` : ''}
          <button class="btn btn-secondary" @click=${this._cancelEdit}>
            Cancel
          </button>
          ${!isNew ? html`
            <span class="spacer"></span>
            ${!this._getEditingProvider()?.is_default ? html`
              <button class="btn btn-default" @click=${this._handleSetDefault}>
                <i class="fa-sharp fa-light fa-star"></i>
                Set as Default
              </button>
            ` : ''}
            <button class="btn btn-danger" @click=${this._handleDelete}>
              <i class="fa-sharp fa-light fa-trash-can"></i>
              Delete
            </button>
          ` : ''}
        </div>
      </div>
    `;
  }

  /** Called by parent (settings-page) when model picker selects a model */
  setModelFromPicker(modelId) {
    this._form = { ...this._form, model: modelId };
  }

  /* ─── Actions ─── */

  _startAdd(target) {
    const defs = llmService.getProviderDefinitions();
    this._editTarget = target;
    this._editing = 'new';
    this._showApiKey = false;
    this._setAsDefault = false;
    this._form = {
      provider: defs[0].id,
      label: '',
      api_key: '',
      model: defs[0].models[0] || '',
      base_url: ''
    };
  }

  _startEdit(provider, target) {
    if (this._editTarget === target && this._editing === provider.key) {
      this._editTarget = null;
      this._editing = null;
      return;
    }
    this._editTarget = target;
    this._editing = provider.key;
    this._showApiKey = false;
    this._form = {
      provider: provider.provider,
      label: provider.label,
      api_key: '',
      model: provider.model,
      base_url: provider.base_url || '',
      mcp_servers_json: provider.mcp_servers ? JSON.stringify(provider.mcp_servers, null, 2) : '',
      _masked: provider.api_key_masked || ''
    };
  }

  _cancelEdit() {
    this._editTarget = null;
    this._editing = null;
    this._form = {};
  }

  _onProviderChange(providerId) {
    const def = llmService.getProviderDef(providerId);
    const models = def ? def.models : [];
    this._form = {
      ...this._form,
      provider: providerId,
      model: models[0] || ''
    };
  }

  _getEditingProvider() {
    if (!this._editing || this._editing === 'new') return null;
    const list = this._editTarget === 'contextual' ? this.contextualProviders : this.providers;
    return list.find(p => p.key === this._editing);
  }

  async _handleSave() {
    if (this._saving) return;

    const { provider, label, api_key, model } = this._form;
    const isNew = this._editing === 'new';
    const isContextual = this._editTarget === 'contextual';

    if (!provider || !label || !model) {
      this._dispatch('toast', { message: 'Provider, label, and model are required', type: 'error' });
      return;
    }
    const selectedDef = llmService.getProviderDefinitions().find(d => d.id === provider);
    if (isNew && !api_key && !selectedDef?.noApiKey) {
      this._dispatch('toast', { message: 'API key is required for new providers', type: 'error' });
      return;
    }

    this._saving = true;
    try {
      const payload = { provider, label, model };
      if (api_key) payload.api_key = api_key;
      if (this._form.base_url) payload.base_url = this._form.base_url;
      // MCP servers config (for local_claude_mcp provider)
      if (this._form.mcp_servers_json) {
        try {
          payload.mcp_servers = JSON.parse(this._form.mcp_servers_json);
        } catch (e) {
          this._dispatch('toast', { message: 'Invalid MCP servers JSON', type: 'error' });
          this._saving = false;
          return;
        }
      }

      let result;
      if (isNew) {
        result = isContextual
          ? await llmService.createContextualProvider(payload)
          : await llmService.createProvider(payload);
      } else {
        result = isContextual
          ? await llmService.updateContextualProvider(this._editing, payload)
          : await llmService.updateProvider(this._editing, payload);
      }

      if (result.success) {
        const savedKey = result.key || this._editing;
        if (isNew && this._setAsDefault && savedKey) {
          try {
            if (isContextual) {
              await llmService.setContextualDefault(savedKey);
            } else {
              await llmService.setDefault(savedKey);
            }
          } catch (e) { /* ignore */ }
        }
        this._editTarget = null;
        this._editing = null;
        this._form = {};
        this._setAsDefault = false;
        this._dispatch('provider-saved', { target: isContextual ? 'contextual' : 'main', key: savedKey });
        this._dispatch('toast', { message: isNew ? 'Provider added' : 'Provider updated' });
      } else {
        this._dispatch('toast', { message: result.error || 'Save failed', type: 'error' });
      }
    } catch (err) {
      this._dispatch('toast', { message: 'Network error saving provider', type: 'error' });
    } finally {
      this._saving = false;
    }
  }

  async _handleDelete() {
    if (this._deleting || this._editing === 'new') return;
    const label = this._form.label || 'this provider';
    const confirmed = await confirmService.show({
      title: 'Delete Provider',
      message: `Delete "${label}"?`,
      description: 'This cannot be undone. Any agents using this provider will fall back to the default.',
    });
    if (!confirmed) return;
    const isContextual = this._editTarget === 'contextual';
    const key = this._editing;
    this._deleting = true;
    try {
      const result = isContextual
        ? await llmService.deleteContextualProvider(key)
        : await llmService.deleteProvider(key);
      if (result.success) {
        this._editTarget = null;
        this._editing = null;
        this._form = {};
        this._dispatch('provider-deleted', { target: isContextual ? 'contextual' : 'main', key });
        this._dispatch('toast', { message: 'Provider deleted' });
      } else {
        this._dispatch('toast', { message: result.error || 'Delete failed', type: 'error' });
      }
    } catch (err) {
      this._dispatch('toast', { message: 'Network error deleting provider', type: 'error' });
    } finally {
      this._deleting = false;
    }
  }

  async _handleSetDefault() {
    if (this._editing === 'new') return;
    const isContextual = this._editTarget === 'contextual';
    try {
      const result = isContextual
        ? await llmService.setContextualDefault(this._editing)
        : await llmService.setDefault(this._editing);
      if (result.success) {
        this._dispatch(isContextual ? 'contextual-default-changed' : 'default-changed', { key: this._editing });
        this._dispatch('toast', { message: 'Default provider updated' });
      } else {
        this._dispatch('toast', { message: result.error || 'Failed to set default', type: 'error' });
      }
    } catch (err) {
      this._dispatch('toast', { message: 'Network error', type: 'error' });
    }
  }

  async _toggleContextualEnabled() {
    if (this._togglingContextual) return;
    this._togglingContextual = true;
    const newVal = !this.contextualEnabled;
    try {
      this._dispatch('contextual-toggled', { enabled: newVal });
    } finally {
      this._togglingContextual = false;
    }
  }

  _dispatch(event, detail = {}) {
    this.dispatchEvent(new CustomEvent(event, { detail, bubbles: true, composed: true }));
  }
}

customElements.define('llm-settings', LlmSettings);
